import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const texts = {
  title: "Partnerschaft",
  heroImage: "https://net-basis.net/Bilder/netbasis-partnership-dach-market-entry.webp",
  heroAlt: "Geschäftsmann reicht die Hand für vertrauensvolle Partnerschaft und Markteintritt im DACH-Markt.",
  heroClaimLine1: "Stark durch",
  heroClaimLine2: "Partnerschaft!",
  h1Text: "Ihr verlässliches Bindeglied für den internationalen Markterfolg. Wir überbrücken kulturelle, sprachliche und logistische Hürden, um ausländische ITK-Hersteller sicher und effizient in der DACH-Region zu etablieren.",
  introParagraphs: [
    "In dem Augenblick, in dem Worte wie „International\" oder „Ausland\" in Ihren unternehmerischen Überlegungen und Betätigungen auftauchen, werden die sich daran anschließenden Prozesse in der Regel komplexer. Unzählige Faktoren müssen bedacht werden: Sprachbarrieren, unübersichtliche rechtliche Situationen oder auch erhebliche Zeitunterschiede können auftreten und eingespielte Planungen durchkreuzen. Nicht zuletzt die Schwierigkeit, persönlich vor Ort anwesend zu sein, um zu sehen, was wirklich geschieht, stellt für viele Hersteller eine große Hürde dar.",
    "Genau an diesem Punkt ist NetBasis International der richtige Partner an Ihrer Seite. Als langjährig etabliertes und sehr erfahrenes Beratungs-, Import- und Handelsunternehmen fungieren wir als Ihr starkes lokales Bindeglied. Wir agieren als Ihr qualifizierter Vermittler vor Ort und bewahren Sie zuverlässig vor kritischen oder geschäftsbeendenden Fehlern, die beim Einstieg in den internationalen Markt entstehen können.",
    "Unsere internationalen Herstellerpartner profitieren unmittelbar von unserem tiefen Marktwissen und unserer festen Präsenz im gesamten deutschsprachigen Wirtschaftsraum. Als Ihr operativer Importeur bringen wir Sie in nur einem einzigen Schritt in direkten Kontakt mit allen relevanten lokalen Vertriebsgesellschaften und Handelsorganisationen. Mit NetBasis International und den damit verbundenen, hochkarätigen Kontakten wird die Markteinführung hervorragender Produkte und zukunftsweisender ITK-Lösungen deutlich einfacher und effizienter.",
    "Gleichzeitig schätzen unsere lokalen Vertriebspartner die Zusammenarbeit mit einem in Deutschland ansässigen Unternehmen wie NetBasis International. Sie erhalten dadurch sofortigen und unkomplizierten Zugriff auf ein breit gefächertes Portfolio innovativer Produkte und Lösungen verschiedener ausländischer Hersteller. Wir sind stolz darauf, bereits mit einer Vielzahl bedeutender, nicht miteinander im Wettbewerb stehender Hersteller erfolgreich zusammenzuarbeiten.",
  ],
  experienceHeading: "Erfahrungsschatz",
  experienceParagraphs: [
    "Märkte verändern sich — das Wissen, die Kontakte und die Erfahrung bleiben.",
    "Seit fast zwei Jahrzehnten baut NetBasis International ausländische Hersteller und Tech-Marken in der DACH-Region auf, etabliert sie im Markt und betreut sie langfristig. Dabei geht es nie nur um reinen Vertrieb. Es geht um Vertrauen und das tiefe Verstehen beider Seiten — der globalen Marke, die auf einem neuen Markt Fuß fassen will, und des lokalen Kunden, der einen verlässlichen Partner sucht.",
    "Gute und verlässliche Beziehungen zu unseren Partnern sind kein Nice-to-have — sie sind das Fundament für nachhaltigen Erfolg und zufriedene Kunden.",
    "Hersteller kommen und gehen, aber die Fähigkeit, Märkte zu lesen und die richtigen Verbindungen zu schaffen, bleibt. Nachfolgend sehen Sie einen Auszug wichtiger Partnerships der letzten fast zwanzig Jahre.",
  ],
  manufacturers: [
    {
      name: "LeadDoubler ApS",
      logo: "https://net-basis.net/Bilder/Lead-Doubler-logo.png",
      desc: "Dänischer Hersteller einer Leadgenerierungs-Software",
    },
    {
      name: "Mondago Ltd",
      logo: "https://net-basis.net/Bilder/Mondago-logo.png",
      desc: "Englischer Hersteller von CTI-Lösungen",
    },
    {
      name: "Pinewood",
      logo: "https://net-basis.net/Bilder/Pinewood-logo.jpg",
      desc: "Belgischer Hersteller von Multimedia-Lösungen",
    },
    {
      name: "Retell Call Recording",
      logo: "https://net-basis.net/Bilder/Re-Tell-logo.png",
      desc: "Englischer Hersteller von Call-Recording-Lösungen",
    },
    {
      name: "Rohde & Schwarz Topex",
      logo: "https://net-basis.net/Bilder/Rohde-Schwarz-logo.jpg",
      desc: "Rumänischer Hersteller von GSM-Gateways",
    },
    {
      name: "Samsung",
      logo: "https://net-basis.net/Bilder/Samsung-logo.png",
      desc: "Zusammenarbeit im Bereich ihrer CTI-Lösung",
    },
    {
      name: "Voice gear",
      logo: "https://net-basis.net/Bilder/Voice-Gear-logo.jpg",
      desc: "Internationale Handelsmarke für Audio-Lösungen",
    },
    {
      name: "Zenion",
      logo: "https://net-basis.net/Bilder/Zenion-logo.jpg",
      desc: "Internationale Handelsmarke für lüfterlose Mini-PCs",
    },
  ],
  claim: "NetBasis International - drive to excellence with excellent drivers !",
};

const renderWithBold = (text: string) => {
  const parts = text ? text.split(COMPANY_NAME) : [];
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <strong className="text-primary-600">{COMPANY_NAME}</strong>
      )}
    </span>
  ));
};

export default function PartnershipPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Partnerschaft | NetBasis International"
        description="Etablierte B2B-Partnerschaften und tiefer Erfahrungsschatz in der DACH-Region. Wir schaffen die richtigen Marktverbindungen für nachhaltigen Erfolg."
        ogUrl="https://net-basis.net/de/partnerschaft"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={texts.heroImage}
                alt={texts.heroAlt}
                title={texts.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-end justify-center px-6 md:px-10 pointer-events-none">
                <div className="flex flex-col items-end -translate-y-14 md:-translate-y-28">
                  <p
                    className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right animate-[bdTextSlide_12s_ease-in-out_infinite]"
                    style={{
                      fontFamily: '"Open Sans", sans-serif',
                      paddingRight: '0.5rem',
                    }}
                  >
                    <span className="block">{texts.heroClaimLine1}</span>
                    <span className="block">{texts.heroClaimLine2}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* WIDGET 1 - Überschrift (jetzt h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {texts.title}
            </h2>

            {/* WIDGET 2 - H1: fetter Fließtext */}
            <h1 className="text-foreground-950 font-semibold text-sm md:text-base leading-relaxed mb-4">
              {texts.h1Text}
            </h1>

            {/* WIDGET 3 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.introParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={idx}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* WIDGET 4 - Überschrift (h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {texts.experienceHeading}
            </h2>

            {/* WIDGET 5 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.experienceParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={`exp-${idx}`}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* Partner-Logos - UNVERÄNDERT */}
            <div className="mt-8 md:mt-10 space-y-6 md:space-y-8">
              {texts.manufacturers.map((m, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-6 md:gap-10"
                >
                  <div className="overflow-hidden w-36 h-24 sm:w-40 sm:h-28 md:w-48 md:h-32 flex-shrink-0 flex items-center justify-center">
                    <img loading="lazy"
                      src={m.logo}
                      alt=""
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm sm:text-lg md:text-xl font-bold text-primary-600 mb-1">
                      {m.name}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground-950 leading-relaxed">
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {texts.claim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}